import { SiteUrls } from '@tableau/shared-urls';
import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '~common/react/CommonStyles';
import { AlertActions } from '~src/dda/common/AlertActions';
import { HeaderStyles } from '~src/spacesPlatform/components/header/HeaderStyles';

import { getAppStore } from '../../../app/AppStore';
import { Formatters, Messages } from '../../../codegen/Localize';
import { showResumeRunFlowTasksActionDialog } from '../../../flow/actions/ResumeRunFlowTasksAction';
import { RunFlowTasks } from '../../../flow/resources/RunFlowTasks';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { SliceableHelper } from '../../../ts/helpers/SliceableHelper';
import { Set } from '../../../ts/models/Set';
import { IAlertInfo, IAlertListInfo } from '../../models/IAlertInfo';
import { AlertsMenuItem } from './AlertsMenuItem';

export type AlertsMenuPanelProps = {
  alertActions: AlertActions;
  alertFailureCountThreshold: number;
  alertFailureCountEnabled: boolean;
  alertLookBackDaysLimit: number;
  flowFailureThresholdForRunPrevention: number;
  itemSelected: () => void;
  siteUrls: SiteUrls;
};

export type AlertsMenuPanelState = {
  alertListInfo: IAlertListInfo;
};

export class AlertsMenuPanel extends React.PureComponent<AlertsMenuPanelProps, AlertsMenuPanelState> {
  static element = React.createFactory(AlertsMenuPanel);
  private readonly sessionManagementService: SessionManagementService;
  private static alertsMenuContainerStyle = _.extend({}, HeaderStyles.MenuPanelStyle, {
    width: 350,
    height: 'inherit',
    maxHeight: 600,
    overflow: 'auto'
  });
  private unsubscribeFromAppStore: () => void;
  private boundOnAppStoreChange = this.onAppStoreChange.bind(this);

  constructor(props: AlertsMenuPanelProps) {
    super(props);
    this.sessionManagementService = getSessionManagementServiceInstance();
    this.state = { alertListInfo: null };
  }

  public componentDidMount() {
    this.unsubscribeFromAppStore = getAppStore().subscribe(this.boundOnAppStoreChange);
    this.props.alertActions.getAlerts();
  }

  public componentWillUnmount() {
    this.unsubscribeFromAppStore();
  }

  private onAppStoreChange() {
    this.setState({ alertListInfo: getAppStore().getState().alertListInfo });
  }

  private resumeRunFlowTasks = (flowId: string) => {
    const runFlowTasks = RunFlowTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.flow(flowId));
    SliceableHelper.fetchAll(runFlowTasks).then(tasks => {
        const suspendedTasks = tasks.items.filter((task) => {
          return task.suspended;
        });
        showResumeRunFlowTasksActionDialog(Set.fromIdItemArray(suspendedTasks));
      });
  }

  public render() {
    return <div style={AlertsMenuPanel.alertsMenuContainerStyle}>{this.alertItems()}</div>;
  }

  private suspendedMessageWarning() {
    if (this.props.alertFailureCountEnabled || this.props.flowFailureThresholdForRunPrevention) {
      return (
          <div className={'tb-alert-suspended-note'}>
            <h1 className={'hidden-but-screenread'}>
              {Messages.aboutSuspended()}
            </h1>
            {this.props.alertFailureCountEnabled ?
            <div className={'tb-alert-suspended-note-message'}>
              {Messages.suspendedJobFailure({
                COUNT: this.props.alertFailureCountThreshold,
                COUNT_LOC: Formatters.formatCount(this.props.alertFailureCountThreshold)
              })}
            </div> :
            null}
            {this.props.flowFailureThresholdForRunPrevention && this.props.flowFailureThresholdForRunPrevention > 0 ?
            <div className={'tb-alert-suspended-note-message'}>
              {Messages.suspendedJobFailureFlows({
                COUNT: this.props.flowFailureThresholdForRunPrevention,
                COUNT_LOC: Formatters.formatCount(this.props.flowFailureThresholdForRunPrevention)
              })}
            </div> :
            null}
          </div>
      );
    } else {
      return null;
    }
  }

  private alertItems(): React.ReactElement<any> {
    const alertList = this.state.alertListInfo;

    // Alerts pending
    if (!alertList || !alertList.alerts) {
      return (
        <div style={{ padding: CommonStyles.Sizes.DefaultSpace, margin: CommonStyles.Sizes.MediumSpace, textAlign: 'center' }}>
          <ActivitySpinnerWidget
            spinnerSize={ActivitySpinnerSize.Small}
            testId="alert-menu-spinner"
          />
        </div>
      );
    }

    // No alerts
    if (alertList.alerts.length === 0) {
      return (
        <div style={{ padding: CommonStyles.Sizes.DefaultSpace, fontStyle: 'italic' }}>
          {Messages.noAlerts()}
        </div>
      );
    }

    return (
      <>
      <div>
        {alertList.alerts.map((alert: IAlertInfo, idx: number) =>
          this.sessionManagementService.flowsOnSiteEnabled() || alert.alertType !== 'FlowRunFailure' ?
            <div key={`alert-menu-item-${idx}`}>
            <AlertsMenuItem
              alert={alert}
              alertActions={this.props.alertActions}
              alertFailureCountEnabled={this.props.alertFailureCountEnabled}
              alertFailureCountThreshold={this.props.alertFailureCountThreshold}
              alertLookBackDaysLimit={this.props.alertLookBackDaysLimit}
              flowFailureThresholdForRunPrevention={this.props.flowFailureThresholdForRunPrevention}
              itemSelected={this.props.itemSelected}
              resumeRunFlowTasks={this.resumeRunFlowTasks}
              siteUrls={this.props.siteUrls}
            /></div> :
            null)
        }
      </div>
    {this.suspendedMessageWarning()}
    </>
    );
  }
}
